/**
 * karmabooking-api
 *
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2020 Karmabunny
 */

import { api } from "./base";
import { BookingStatus } from "./booking";


export type PaymentStatus =
    | 'pending'     // - not even started
    | 'received'    // - currently processing
    | 'requires_action' // - 3DS stuff
    | 'processed'   // - payment complete
    | 'refunded'    // - payment reversed
    | 'error'       // - bad
    | 'cancelled'   // - cancelled before complete
;


export type GatewayType = PaymentJson['gateway'];

interface BasePaymentJson {
    paymentId: string;
    amount: number;
    currencyCode: string;
    status: PaymentStatus;
    test: boolean;
    gateway: unknown;
    booking: {
        bookingId: string;
        reservationId: number;
        status: BookingStatus;
    }
}

export type PaymentJson =
    | DumbPaymentJson
    | CbaPaymentJson
    | StripePaymentJson
    | PxPaymentJson
    | VgsPaymentJson
;

export interface CbaPaymentJson extends BasePaymentJson {
    gateway: 'cba';
    cba: {
        merchant_id: string;
        session_id: string;
        cba_version: string;
    }
}

export interface StripePaymentJson extends BasePaymentJson {
    gateway: 'stripe3';
    stripe: {
        public_key: string;
        payment_intent_id: string;
        payment_method_id?: string;
        payment_intent_secret?: string;
    }
}

export interface PxPaymentJson extends BasePaymentJson {
    gateway: 'pxrest';
    px: {
        sessionId: string;
        ajaxSubmitCard?: string;
        transactionId?: string;
    }
}

export interface DumbPaymentJson extends BasePaymentJson {
    gateway: 'dumb';
}

export interface VgsPaymentJson extends BasePaymentJson {
    gateway: 'vgs';
}


export interface StripePaymentOptions {
    payment_method_id: string;
}

export interface PaymentConfigJson {
    test: boolean;
    status: PaymentStatus;
    gateway: GatewayType;
    stripe?: {
        public_key: string;
    }
    px?: {}
    cba?: {
        merchant_id: string;
        cba_version: string;
    }
    vgs?: {}
}


export async function startPayment(bookingId: string) {
    const res = await api({
        method: "GET",
        endpoint: "/book/api/payment.json",
        query: { bookingId },
    });

    return res as PaymentConfigJson;
}


export async function createPayment(gateway: 'dumb', bookingId: string): Promise<DumbPaymentJson>;
export async function createPayment(gateway: 'cba', bookingId: string): Promise<CbaPaymentJson>;
export async function createPayment(gateway: 'pxrest', bookingId: string): Promise<PxPaymentJson>;
export async function createPayment(gateway: 'stripe3', bookingId: string): Promise<StripePaymentJson>;
export async function createPayment(gateway: 'vgs', bookingId: string): Promise<VgsPaymentJson>;
export async function createPayment(gateway: GatewayType, bookingId: string): Promise<PaymentJson> {
    const res = await api<PaymentJson>({
        method: "POST",
        endpoint: "/book/api/payment.json",
        body: { bookingId },
    });

    if (res.gateway !== gateway) {
        throw new Error('Invalid gateway');
    }

    return res;
}


export async function processPayment<T extends PaymentJson = PaymentJson>(paymentId: string, options?: any) {
    const res = await api({
        method: "PUT" ,
        endpoint: "/book/api/payment.json",
        query: { paymentId },
        body: options || {},
    });

    return res as T;
}
